import React, { useState, useEffect } from "react";
import { Icon } from "../../../components/Component";
import { useNavigate } from "react-router";
import { FormProvider, useForm } from "react-hook-form";
import {
  Card,
  Skeleton,
  Avatar,
  Input,
  Drawer,
  Empty,
  message,
  Flex,
  Spin,
  Tag,
  Badge,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  LoadingOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  AppstoreOutlined,
  UserOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import checklistApis from "../../../api/checklist/checklistapi";
import SearchInput from "../../form-components/SearchComponent";
import { Button, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import Head from "../../../layout/head/Head";
import { useDispatch } from "react-redux";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import Content from "../../../layout/content/Content";
import { Alert } from "reactstrap";
import { useLocation } from "react-router";

const CheckListLists = () => {
  const methods = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [openedChecklist, setOpenedChecklist] = useState<string | null>(null);
  const [endOfList, setEndOfList] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],

    total: 0,
  });

  const filteredData = searchQuery
    ? originalData.filter(item =>
      item.checklistName.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : originalData;

  const refreshListView = () => {
    try {
      setEndOfList(false);
      setPagination({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData(); // calling for initial data load , if the view type is table
    } catch (error) { }
  };

  const getColorFromName = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 160}, 20%, 50%)`;
  };

  useEffect(() => {
    fetchAllChecklists();
  }, [])

  useEffect(() => {
    refreshListView();
    dispatch(updateMenuData(TopMenu?.ChecklistList));
  }, [location]);

  const fetchAllChecklists = async () => {
    try {
      setLoading(true);
      const response = await checklistApis.fetchChecklist({
        direction: "P",
        sortOrder: "DESC",
        pageDiff: 0,
        pageSize: 1000,
      });

      if (!response || response.status === false) throw new Error("API error");

      const items = response.data?.list || [];
      const validList = items.filter((item: any) => item && item.checklistId);
      setOriginalData(validList);
    } catch (err) {
      message.error("Failed to load all checklists.");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    fetchAllChecklists();
    setSearchQuery("");
  };

  const handleChecklistClick = (checklistId: string) => {
    setOpenedChecklist(checklistId);
  };

  const handleCloseChecklist = () => {
    setOpenedChecklist(null);
  };

  const handleOpenAddModal = () => {
    navigate('/pages/Checklist/create');
  };

  const handleOpenEditModal = (checklistId: string) => {
    navigate('/pages/Checklist/edit', {
      state: {
        checklistId
      }
    });
  };

  return (
    <>
      <Head title="Checklist" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <FormProvider {...methods}>
                <form>
                  <div
                    className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                    style={{ minHeight: "40px", border: "0px" }}
                  >
                    <div className="nk-ibx-head-actions w-45 ms-1">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                        <div className={`w-100 m-0 `}>
                          <SearchInput
                            name="search"
                            placeholder="Search..."
                            width="100%"
                            prefixIcon={<SearchOutlined />}
                            onDebouncedSearch={(value: any) => {
                              setSearchQuery(value);
                            }}
                            debounceDelay={300}
                          />
                        </div>
                      </ul>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        className="btn btn-primary btn-l"
                        color="primary"
                        size="sm"
                        onClick={handleOpenAddModal}
                      >
                        <Icon name={"plus"} />
                        <span>Add Checklist</span>
                      </Button>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="a"
                          className="dropdown-toggle btn btn-icon btn-trigger"
                          onClick={refreshData}
                        >
                          <Icon name="redo"></Icon>
                        </DropdownToggle>
                      </UncontrolledDropdown>
                    </div>
                  </div>
                </form>
              </FormProvider>
              <div className="card-inner card-inner-lg p-0">
                <div className="content clearfix">
                  <div style={{ padding: "10px 20px 20px 0" }}>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(280px, 1fr))",
                      gap: "20px",
                      maxHeight: "calc(100vh - 200px)",
                      overflowY: "auto",
                      padding: "5px 15px 15px 0"
                    }}>
                      {filteredData?.map((checklist: any) => {
                        if (!checklist) return null;
                        const isHovered = hoveredCardId === checklist.checklistId;
                        const questionsCount = checklist.checklistQuestions?.length || checklist.questions?.length || 0;

                        return (
                          <Card
                            key={checklist.checklistId}
                            style={{
                              borderRadius: 10,
                              backgroundColor: openedChecklist === checklist.checklistId ? "#277af714" : "#fff",
                              border: openedChecklist === checklist.checklistId ? "1px solid #277af733" : "1px solid #e0e0e0",
                              boxShadow: openedChecklist === checklist.checklistId ? "0 10px 8px #00000014" : "0 2px 8px rgba(0,0,0,0.1)",
                              padding: 16,
                              position: "relative",
                              transition: "all 0.3s ease",
                              cursor: "pointer"
                            }}
                            onClick={() => handleChecklistClick(checklist.checklistId)}
                            onMouseEnter={() => setHoveredCardId(checklist.checklistId)}
                            onMouseLeave={() => setHoveredCardId(null)}
                            bodyStyle={{
                              display: "flex",
                              alignItems: "center",
                              padding: 0,
                            }}
                          >
                            <Avatar
                              size={40}
                              style={{
                                backgroundColor: getColorFromName(checklist.checklistName),
                                fontWeight: "bold",
                                fontSize: 18,
                                marginRight: 16,
                              }}
                            >
                              {checklist.checklistName
                                .split(" ")
                                .map((word: any) => word[0])
                                .join("")
                                .toUpperCase()
                                .slice(0, 2)}
                            </Avatar>
                            <div style={{ flex: 1 }}>
                              <>
                                <div style={{ fontWeight: 500 }}>
                                  {checklist.checklistName}
                                </div>
                                <div style={{ color: "#888" }}>
                                  {questionsCount} Check Points
                                </div>
                                <div style={{ color: "#888", fontSize: 12 }}>
                                  Created by : {checklist.createdByUserName || "Admin"}
                                </div>
                              </>
                            </div>

                            {isHovered && checklist.isUserDefined === 1 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 12,
                                  right: 12,
                                  display: "flex",
                                  gap: 8,
                                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                                  padding: "6px 10px",
                                  borderRadius: "6px",
                                  zIndex: 10
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Icon
                                  name="edit"
                                  className="icon ni ni-edit"
                                  style={{
                                    color: "#4169E1",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    padding: "2px",
                                  }}
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleOpenEditModal(checklist.checklistId);
                                  }}
                                />
                              </div>
                            )}
                          </Card>
                        );
                      })}

                      {loading &&
                        Array.from({ length: 8 })?.map((_, index) => (
                          <Card key={index} style={{ minHeight: '120px' }}>
                            <Skeleton active />
                          </Card>
                        ))}

                      {filteredData.length === 0 && !loading && (
                        <div style={{
                          width: '100%',
                          textAlign: 'center',
                          padding: 40,
                          gridColumn: '1 / -1'
                        }}>
                          <Empty description="No checklists found" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
        {/* </div>
        </div> */}

        <Drawer
          title="Checklist Preview"
          placement="right"
          width={760}
          open={!!openedChecklist}
          onClose={handleCloseChecklist}
          destroyOnClose
          styles={{ body: { padding: 0, background: "#fafbfc" } }}
        >
          {openedChecklist && (
            <PreviewChecklistModal
              checklistId={openedChecklist}
              onClose={handleCloseChecklist}
            />
          )}
        </Drawer>
      </Content>
    </>
  );
};

const PreviewChecklistModal = ({ checklistId, onClose }: { checklistId: string; onClose: () => void }) => {
  const [checklistData, setChecklistData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchChecklistDetails = async () => {
    try {
      const response = await checklistApis.fetchChecklistById(checklistId);
      if (response.data) {
        setChecklistData(response.data);
      }
    } catch (error) {
      console.error("Error fetching checklist details:", error);
      message.error("Failed to load checklist details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecklistDetails();
  }, [checklistId]);

  // Map question type → display label, color, and icon for the type badge.
  const getTypeMeta = (type: string) => {
    const t = (type || "").toLowerCase();
    if (t === "text") {
      return {
        label: "Text",
        color: "blue",
        icon: <FileTextOutlined />,
      };
    }
    if (t === "single_choice" || t === "single_selection") {
      return {
        label: "Single Choice",
        color: "geekblue",
        icon: <CheckSquareOutlined />,
      };
    }
    if (t === "multiple_choice" || t === "multiple_selection") {
      return {
        label: "Multiple Choice",
        color: "purple",
        icon: <AppstoreOutlined />,
      };
    }
    return {
      label: type || "Unknown",
      color: "default",
      icon: <FileTextOutlined />,
    };
  };

  const renderPreviewQuestion = (question: any) => {
    const type = (question.questionType || question.type || "").toLowerCase();

    if (type === "text") {
      return (
        <Input.TextArea
          placeholder="User's free-text answer will appear here"
          autoSize={{ minRows: 2, maxRows: 4 }}
          disabled
          style={{ background: "#fff" }}
        />
      );
    }

    if (
      type === "single_choice" ||
      type === "single_selection" ||
      type === "multiple_choice" ||
      type === "multiple_selection"
    ) {
      const options = question.optionsType
        ? Object.entries(question.optionsType).map(([value, label]) => ({
            label: String(label).replace(/"/g, ""),
            value: value as string,
          }))
        : [];
      const isMulti = type.startsWith("multiple");
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {options.map((option: any) => (
            <div
              key={option.value}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                borderRadius: 20,
                background: "#f5f7fa",
                border: "1px solid #e5e7eb",
                fontSize: 13,
                color: "#374151",
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: isMulti ? 3 : "50%",
                  border: "1.5px solid #9ca3af",
                  display: "inline-block",
                }}
              />
              <span style={{ fontWeight: 500, color: "#6b7280" }}>
                {option.value}.
              </span>
              {option.label}
            </div>
          ))}
          {options.length === 0 && (
            <span style={{ color: "#9ca3af", fontSize: 12 }}>
              No options configured
            </span>
          )}
        </div>
      );
    }

    return (
      <span style={{ color: "#9ca3af", fontSize: 12 }}>
        Unknown question type: {type}
      </span>
    );
  };

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ minHeight: 320, padding: 40 }}
      >
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </Flex>
    );
  }

  if (!checklistData) {
    return (
      <div style={{ padding: 24 }}>
        <Alert color="danger">
          Failed to load checklist data.
          <Button color="link" onClick={onClose}>
            Close
          </Button>
        </Alert>
      </div>
    );
  }

  const questions = checklistData.checklistQuestions || [];
  const mandatoryCount = questions.filter((q: any) => q.isMandatory).length;
  const typeBreakdown: Record<string, number> = questions.reduce(
    (acc: Record<string, number>, q: any) => {
      const key = (q.questionType || q.type || "unknown").toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    {}
  );

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* Gradient Header Card */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          borderRadius: 12,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
        }}
        styles={{ body: { padding: "18px 20px" } }}
      >
        <div style={{ color: "#fff" }}>
          <h4
            style={{
              margin: 0,
              color: "#fff",
              fontSize: 20,
              fontWeight: 600,
              wordBreak: "break-word",
            }}
          >
            {checklistData.checklistName}
          </h4>
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginTop: 10,
              fontSize: 13,
              color: "rgba(255,255,255,0.9)",
            }}
          >
            <span>
              <UserOutlined style={{ marginRight: 6 }} />
              Created by{" "}
              <strong>
                {checklistData.createdByUserName || "Admin"}
              </strong>
            </span>
            {checklistData.createdAt ? (
              <span>
                <CalendarOutlined style={{ marginRight: 6 }} />
                {new Date(checklistData.createdAt).toLocaleDateString(
                  "en-GB",
                  { day: "2-digit", month: "short", year: "numeric" }
                )}
              </span>
            ) : null}
            {checklistData.isUserDefined !== undefined ? (
              <Tag color={checklistData.isUserDefined ? "gold" : "blue"}>
                {checklistData.isUserDefined ? "User Defined" : "System"}
              </Tag>
            ) : null}
          </div>
        </div>
      </Card>

      {/* Summary stats row */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            flex: "1 1 140px",
            background: "#f0f9ff",
            border: "1px solid #bae0ff",
            borderRadius: 10,
            padding: "10px 14px",
          }}
        >
          <div style={{ fontSize: 12, color: "#0958d9" }}>Check Points</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: "#0958d9" }}>
            {questions.length}
          </div>
        </div>
        <div
          style={{
            flex: "1 1 140px",
            background: "#fff1f0",
            border: "1px solid #ffa39e",
            borderRadius: 10,
            padding: "10px 14px",
          }}
        >
          <div style={{ fontSize: 12, color: "#cf1322" }}>Mandatory</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: "#cf1322" }}>
            {mandatoryCount}
          </div>
        </div>
        {Object.keys(typeBreakdown).map((tk) => {
          const meta = getTypeMeta(tk);
          return (
            <div
              key={tk}
              style={{
                flex: "1 1 140px",
                background: "#fafafa",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <div style={{ fontSize: 12, color: "#666" }}>
                <Tag color={meta.color} style={{ margin: 0 }}>
                  {meta.icon} {meta.label}
                </Tag>
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  color: "#262626",
                  marginTop: 4,
                }}
              >
                {typeBreakdown[tk]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Question cards */}
      {questions.length === 0 ? (
        <Empty description="No questions have been added yet" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {questions.map((question: any, index: number) => {
            const meta = getTypeMeta(
              question.questionType || question.type || ""
            );
            return (
              <Card
                key={question.questionId || index}
                size="small"
                style={{
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                }}
                styles={{ body: { padding: "14px 16px" } }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <Badge
                    count={index + 1}
                    style={{
                      backgroundColor: "#1677ff",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                    overflowCount={999}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <Tooltip title={meta.label}>
                        <Tag color={meta.color} style={{ margin: 0 }}>
                          {meta.icon} {meta.label}
                        </Tag>
                      </Tooltip>
                      {question.isMandatory ? (
                        <Tag color="red" style={{ margin: 0 }}>
                          Required
                        </Tag>
                      ) : (
                        <Tag color="default" style={{ margin: 0 }}>
                          Optional
                        </Tag>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#1f2937",
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                      }}
                    >
                      {question.questionName || question.name}
                    </div>
                    {question.description ? (
                      <div
                        style={{
                          marginTop: 4,
                          color: "#6b7280",
                          fontSize: 12.5,
                        }}
                      >
                        {question.description}
                      </div>
                    ) : null}
                    <div style={{ marginTop: 10 }}>
                      {renderPreviewQuestion(question)}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CheckListLists;